'use client';

import { useTheme } from '@/contexts/ThemeContext';
import { CurrencyType, useCurrency } from '@/hooks/useCurrency';
import { Language } from '@/lib/translations';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

interface FooterSwitchersProps {
  lang: Language;
}

export default function FooterSwitchers({ lang }: FooterSwitchersProps) {
  const { currency, setCurrency } = useCurrency();
  const { theme, setTheme } = useTheme();
  const router = useRouter();
  const pathname = usePathname();
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isCurrencyOpen, setIsCurrencyOpen] = useState(false);
  const [isThemeOpen, setIsThemeOpen] = useState(false);
  const languageRef = useRef<HTMLDivElement>(null);
  const currencyRef = useRef<HTMLDivElement>(null);
  const themeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        languageRef.current &&
        !languageRef.current.contains(event.target as Node)
      ) {
        setIsLanguageOpen(false);
      }
      if (
        currencyRef.current &&
        !currencyRef.current.contains(event.target as Node)
      ) {
        setIsCurrencyOpen(false);
      }
      if (
        themeRef.current &&
        !themeRef.current.contains(event.target as Node)
      ) {
        setIsThemeOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const switchLanguage = (newLang: Language) => {
    // Replace language in current path
    const newPath = pathname.replace(`/${lang}`, `/${newLang}`);
    router.push(newPath);
    setIsLanguageOpen(false);
  };

  const handleCurrencyChange = (newCurrency: CurrencyType) => {
    setCurrency(newCurrency);
    setIsCurrencyOpen(false);
  };

  const handleThemeChange = (newTheme: 'light' | 'dark' | 'system') => {
    setTheme(newTheme);
    setIsThemeOpen(false);
  };

  const getThemeLabel = () => {
    switch (theme) {
      case 'light':
        return lang === 'en' ? 'Light' : '亮色';
      case 'dark':
        return lang === 'en' ? 'Dark' : '暗色';
      case 'system':
        return lang === 'en' ? 'Auto' : '自動';
      default:
        return lang === 'en' ? 'Light' : '亮色';
    }
  };

  return (
    <div className="flex space-x-4 mt-4 sm:mt-0">
      {/* Language Switcher */}
      <div className="relative" ref={languageRef}>
        <button
          onClick={() => setIsLanguageOpen(!isLanguageOpen)}
          className="flex items-center space-x-1 text-gray-400 hover:text-white dark:hover:text-gray-100 text-sm transition-colors cursor-pointer"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
            />
          </svg>
          <span>{lang === 'en' ? 'English' : '中文'}</span>
          <svg
            className="w-3 h-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        {isLanguageOpen && (
          <div className="absolute bottom-full mb-2 right-0 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 py-2 min-w-[120px]">
            <button
              onClick={() => switchLanguage('en')}
              className={`w-full text-left px-4 py-2 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 ${lang === 'en' ? 'text-blue-600 dark:text-blue-400 font-medium' : ''}`}
            >
              English
            </button>
            <button
              onClick={() => switchLanguage('zh')}
              className={`w-full text-left px-4 py-2 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 ${lang === 'zh' ? 'text-blue-600 dark:text-blue-400 font-medium' : ''}`}
            >
              中文
            </button>
          </div>
        )}
      </div>

      {/* Currency Switcher */}
      <div className="relative" ref={currencyRef}>
        <button
          onClick={() => setIsCurrencyOpen(!isCurrencyOpen)}
          className="flex items-center space-x-1 text-gray-400 hover:text-white dark:hover:text-gray-100 text-sm transition-colors cursor-pointer"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
            />
          </svg>
          <span>{currency}</span>
          <svg
            className="w-3 h-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        {isCurrencyOpen && (
          <div className="absolute bottom-full mb-2 right-0 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 py-2 min-w-[130px]">
            <button
              onClick={() => handleCurrencyChange('USD')}
              className={`w-full text-left px-4 py-2 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 ${currency === 'USD' ? 'text-blue-600 dark:text-blue-400 font-medium' : ''}`}
            >
              USD ($)
            </button>
            <button
              onClick={() => handleCurrencyChange('TWD')}
              className={`w-full text-left px-4 py-2 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 ${currency === 'TWD' ? 'text-blue-600 dark:text-blue-400 font-medium' : ''}`}
            >
              TWD (NT$)
            </button>
          </div>
        )}
      </div>

      {/* Theme Switcher */}
      <div className="relative" ref={themeRef}>
        <button
          onClick={() => setIsThemeOpen(!isThemeOpen)}
          data-testid="theme-picker"
          className="flex items-center space-x-1 text-gray-400 hover:text-white dark:hover:text-gray-100 text-sm transition-colors cursor-pointer"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
            />
          </svg>
          <span>{getThemeLabel()}</span>
          <svg
            className="w-3 h-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        {isThemeOpen && (
          <div className="absolute bottom-full mb-2 right-0 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 py-2 min-w-[120px]">
            <button
              onClick={() => handleThemeChange('light')}
              data-testid="theme-light"
              className={`w-full text-left px-4 py-2 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2 ${theme === 'light' ? 'text-blue-600 dark:text-blue-400 font-medium' : ''}`}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
              <span>{lang === 'en' ? 'Light' : '亮色'}</span>
            </button>
            <button
              onClick={() => handleThemeChange('dark')}
              data-testid="theme-dark"
              className={`w-full text-left px-4 py-2 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2 ${theme === 'dark' ? 'text-blue-600 dark:text-blue-400 font-medium' : ''}`}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                />
              </svg>
              <span>{lang === 'en' ? 'Dark' : '暗色'}</span>
            </button>
            <button
              onClick={() => handleThemeChange('system')}
              className={`w-full text-left px-4 py-2 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2 ${theme === 'system' ? 'text-blue-600 dark:text-blue-400 font-medium' : ''}`}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              <span>{lang === 'en' ? 'Auto' : '自動'}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
